import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import clsx from 'clsx';

import * as actions from 'containers/Settings/Leads/actions';
import Select from 'components/FormFields/Select';
import { Loading } from 'components/Loading';
import ConfirmationModal from 'components/Modal/ConfirmationModal';
import Card from './Card';
import './booking-process.scss';

const SortableCard = (({ item, isSortable, updateStatus, deleteStatus, newId }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: item.id,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Card
      {...item}
      style={style}
      isSortable={isSortable}
      updateStatus={updateStatus}
      deleteStatus={deleteStatus}
      key={`modal-reorder__${item.id}`}
      isNew={item.id === newId}
      setNodeRef={setNodeRef}
      attributes={attributes}
      listeners={listeners}
      isDragging={isDragging}
    />
  );
});

const BookingProcess = () => {
  const filters = useSelector(state => state.leads.filters);
  const [items, setItems] = useState([]);
  const [newStatusId, setNewStatusId] = useState(null);
  const [statusToDelete, setStatusToDelete] = useState(null);
  const [migrateToStatus, setMigrateToStatus] = useState(null);
  const isLoading = !Object.prototype.hasOwnProperty.call(items[0] ?? {}, 'isEditable');

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 10 },
  });
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: {
      delay: 250,
      tolerance: 5,
    },
  });

  const sensors = useSensors(
    /*
    useSensor(MouseSensor, {
      activationConstraint: { distance: 10 },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    */
    mouseSensor,
    touchSensor,
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const dispatch = useDispatch();
  const {
    getLeadsStatuses,
    createLeadsStatus,
    updateCustomStatus,
    deleteCustomStatus,
    reorderStatuses,
  } = bindActionCreators(actions, dispatch);

  // When component is mounted, fetch additional data on custom statuses.
  useEffect(() => {
    getLeadsStatuses();
  }, []);

  useEffect(() => {
    // #TODO: This will need to be changed when apollo cache is better utilized.
    // Should be able to determine which status is new when we directly update
    // redux with a new status, instead of refetching whole payload.
    const oldStatusIds = items.map(i => i.id);
    const newStatusIds = filters.map(f => f.id);
    for (let i = 0; i < newStatusIds.length; i+=1) {
      if (!oldStatusIds.includes(newStatusIds[i])) {
        setNewStatusId(newStatusIds[i]);
        break;
      }
    }
    setItems(filters);
  }, [filters, filters.length]);

  const deleteStatus = (id) => {
    // Call confirmation modal to delete.
    setStatusToDelete(id);
  };

  const confirmDelete = () => {
    const statusId = statusToDelete;
    const moveId = migrateToStatus;
    deleteCustomStatus({ statusId, moveId });
    setStatusToDelete(null);
    setMigrateToStatus(null);
  };

  const handleStatusSelect = (e) => {
    const newStatus = parseInt(e.target.value, 10);
    setMigrateToStatus(newStatus);
  };

  const renderConfirmationMessage = () => {
    const index = items.findIndex(i => i.id === statusToDelete);

    return (
      <div className="modal_confirm__container">
        <section className="modal_confirm__content">
          Are you sure?
          {index && items[index].count > 0 ? (
            <>
              <p className="submessage">
                Deleting this status will remove this status from all associated leads.
                Please select which status you would like to reassign the leads to:
              </p>
              <Select
                onChange={handleStatusSelect}
                value={migrateToStatus}
                className="submessage"
                scrollLockOnOpen={false}
              >
                {items.filter(i => i.id !== statusToDelete).map(item => {
                  return (
                    <option
                      key={`move-to-${item.id}`}
                      value={item.id}
                    >
                      {item.name}
                    </option>
                  );
                })}
              </Select>
            </>
          ) : (
            <p className="submessage">
              Deleting this status will permanently remove it from your index.
            </p>
          )}
        </section>
        <section className="modal_confirm__buttons">
          <button
            className="button button--outline button--cancel-confirm button--md"
            onClick={() => setStatusToDelete(null)}
          >
            Cancel
          </button>
          <button
            // In web component, need to set key so prop memos are refreshed.
            // #TODO: This can probably be removed when react is upgraded.
            key={`can-delete-${migrateToStatus}`}
            className="button button--md button--delete-confirm"
            onClick={confirmDelete}
            disabled={items[index].count > 0 && !migrateToStatus}
          >
            Confirm
          </button>
        </section>
      </div>
    );
  };

  const onDragStart = () => {
    // To add a class for css when dragging is occuring.
    const body = document.body || document.getElementsByTagName('body')[0];
    body.classList.add('is-dragging');
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    // Clean up dragging class.
    const body = document.body || document.getElementsByTagName('body')[0];
    body.classList.remove('is-dragging');

    // Prevent any updates if the index did not change.
    if (active.id !== over.id) {
      const newIndex = items.findIndex((i) => i.id === over.id);
      // May need to add extra validation to ensure "new" always stays at the front
      // and "booked" and "lost" at the end, but this should suffice for now.
      reorderStatuses({ statusId: active.id, order: items[newIndex].order });
      setItems((is) => {
        const oldIndex = is.findIndex((i) => i.id === active.id);
        return arrayMove(is, oldIndex, newIndex);
      });
    }
  };

  return (
    <>
      <p className="booking-process__intro">
        Fine tune your booking process by adding, editing or deleting each step below.
        You can also drag and drop to reorder and add a checkmark to specify which
        statuses should appear as current.{' '}
        <a
          href="https://help.aisleplanner.com/en/articles/2324390-customizing-your-booking-process-sales-funnel"
          target="_blank"
          rel="noreferrer"
        >
          Learn more
        </a>
        .
      </p>
      <div
        className={clsx(
          'booking-process__status-list',
          isLoading && 'loading',
          items.length%2 === 1 && 'odd',
        )}
      >
        <DndContext
          sensors={sensors}
          onDragStart={onDragStart}
          onDragEnd={handleDragEnd}
          collisionDetection={closestCenter}
        >
          <SortableContext
            items={items}
            modifiers={restrictToFirstScrollableAncestor}
          >
            {items.map((item, index) => {
              if (item.name !== 'New') {
                return (
                  <SortableCard
                    key={`custom-status-${item.id}`}
                    item={item}
                    index={index}
                    updateStatus={updateCustomStatus}
                    deleteStatus={deleteStatus}
                    isSortable
                    newId={newStatusId}
                  />
                );
              }

              return (
                <Card
                  {...item}
                  key={`modal-card__${item.id}`}
                  isSortable={false}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
      <div className="booking-process__create-status">
        <button
          className="button--unstyled button--add"
          onClick={() => createLeadsStatus({ name: '', order: 1 })}
        >
          Add new status
        </button>
      </div>
      {isLoading ? (
        <Loading />
      ) : null }
      {statusToDelete ? (
        <ConfirmationModal
          visible
          className="ap-modal--confirm"
          size="narrow"
          onClose={() => setStatusToDelete(null)}
        >
          {renderConfirmationMessage()}
        </ConfirmationModal>
      ) : null}
    </>
  );
};

export default BookingProcess;
